import React, { useCallback, useContext, useEffect, useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Typography,
  Checkbox,
} from '@mui/material';

import { registerPushDevice, requestPushPermission } from '../pushDevice';
import {
  fetchApiRequest,
  getApiHost,
  renderBoilerPlate,
} from './Common';
import { UserContext } from './UserContext';

type NotifyVia = 'email' | 'push' | 'both' | 'none';

const NOTIFY_OPTIONS: { value: NotifyVia; label: string; hint: string }[] = [
  {
    value: 'email',
    label: 'Email',
    hint: 'Receive notifications by email.',
  },
  {
    value: 'push',
    label: 'Push',
    hint: 'Mobile or browser push (requires a registered device).',
  },
  {
    value: 'both',
    label: 'Both',
    hint: 'Email and push when available.',
  },
  {
    value: 'none',
    label: 'None',
    hint: 'Do not send personal notifications.',
  },
];

type UserDevice ={
  device_id: string;
  device_name: string;
  device_type: string;
};

const UserPreferencesPage: React.FC<RouteComponentProps> = () => {
  const user = useContext(UserContext);
  const [notifyVia, setNotifyVia] = useState<NotifyVia>('email');
  const [savedNotifyVia, setSavedNotifyVia] = useState<NotifyVia>('email');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [devices, setDevices] = useState<UserDevice[]>([]);
  const [devicesLoading, setDevicesLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sendingTest, setSendingTest] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [browserPermission, setBrowserPermission] = useState<
    NotificationPermission | 'unsupported'
  >('default');

  const apiFetch = useCallback((path: string, init: RequestInit = {}) => {
    return new Promise<Response>((resolve, reject) => {
      getApiHost((apiHost) => {
        fetchApiRequest(`${apiHost}${path}`, (promise) => {
          promise.then(resolve).catch(reject);
        }, init);
      });
    });
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiFetch('/v1/user/preferences')
      .then(async (res) => {
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          const detail = body.detail;
          const msg = typeof detail === 'string'
            ? detail
            : Array.isArray(detail)
              ? detail.map((d) => d.msg || JSON.stringify(d)).join('; ')
              : res.statusText;
          throw new Error(msg || `HTTP ${res.status}`);
        }
        return body;
      })
      .then((data) => {
        if (cancelled) return;
        const value = data.notify_via as NotifyVia;
        setNotifyVia(value);
        setSavedNotifyVia(value);
      })
      .catch((e: Error) => {
        if (!cancelled) setError(String(e.message || e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiFetch]);
  useEffect(() => {
    let cancelled = false;
    setDevicesLoading(true);
    apiFetch('/v1/user/devices')
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled) setDevices(data);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setDevicesLoading(false);
     });
    return () => { cancelled = true; };
  }, [apiFetch]);

  useEffect(() => {
    if (!('Notification' in window)) {
      setBrowserPermission('unsupported');
      return;
    }
    setBrowserPermission(Notification.permission);
  }, []);

  const wantsPush = notifyVia === 'push' || notifyVia === 'both';
  const pushBlocked = browserPermission === 'denied';

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await apiFetch('/v1/user/preferences', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notify_via: notifyVia }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = body.detail;
        const msg = typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d.msg || JSON.stringify(d)).join('; ')
            : res.statusText;
        throw new Error(msg || `HTTP ${res.status}`);
      }
      const value = body.notify_via as NotifyVia;
      setNotifyVia(value);
      setSavedNotifyVia(value);
      setSuccess('Preferences saved.');
    } catch (e: any) {
      setError(String(e.message || e));
    } finally {
      setSaving(false);
    }
  };

  const handleNotifyViaChange = async (value: NotifyVia) => {
    setNotifyVia(value);
    setError(null);
    setSuccess(null);

    if (value !== 'push' && value !== 'both') {
      return;
    }

    if (!('Notification' in window)) {
      setBrowserPermission('unsupported');
      return;
    }

    const permission = await requestPushPermission();
    setBrowserPermission(permission);

    if (permission === 'granted') {
      try {
        await new Promise<void>((resolve, reject) => {
          getApiHost((apiHost) => {
            registerPushDevice(apiHost).then(resolve).catch(reject);
          });
        });
      } catch (e: any) {
        setError(String((e as Error).message || e));
      }
    }
  };

  const handleSendTest = async () => {
    setSendingTest(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await apiFetch('/v1/user/devices/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json'},
        body: JSON.stringify({ device_ids: Array.from(selectedIds) }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.detail || `HTTP ${res.status}`);
      setSuccess(`Sent to ${body.sent.length} device(s).`);
    } catch (e: any) {
      setError(String(e.message || e));
    } finally {
      setSendingTest(false);
    }
  };

  const dirty = notifyVia !== savedNotifyVia;

  return renderBoilerPlate(
    <Box sx={{ p: 3, maxWidth: 640 }}>
      <Typography variant="h5" gutterBottom>
        Notification preferences
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Choose how you want to be notified. This applies to personal alerts;
        org notification rules are configured separately.
      </Typography>
      {user.profile?.email && (
        <Typography variant="body2" sx={{ mb: 2 }}>
          Signed in as {user.profile.email}
        </Typography>
      )}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      {wantsPush && pushBlocked && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Notifications are blocked for this site. Reset them in your browser site
          settings (lock icon in the address bar), then try again.
        </Alert>
      )}
      <Paper sx={{ p: 2 }}>
        {loading ? (
          <Typography>Loading…</Typography>
        ) : (
          <FormControl component="fieldset" disabled={saving}>
            <RadioGroup
              value={notifyVia}
              onChange={(e) => handleNotifyViaChange(e.target.value as NotifyVia)}
            >
              {NOTIFY_OPTIONS.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio />}
                  label={(
                    <Box>
                      <Typography>{option.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.hint}
                      </Typography>
                    </Box>
                  )}
                  sx={{ alignItems: 'flex-start', mb: 1 }}
                />
              ))}
            </RadioGroup>
          </FormControl>
        )}
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading || saving || !dirty}
          sx={{ mt: 2 }}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Paper>
        
      <Paper sx={{ p: 2, mt: 2 }}>
        <Typography variant="h6" gutterBottom>
          Your devices
        </Typography>
        {devicesLoading ? (
          <Typography>Loading devices…</Typography>
        ) : devices.length === 0 ? (
          <Typography color="text.secondary">
            No registered devices. Enable push and allow notifications.
          </Typography>
        ) : (
          devices.map((device) => (
            <FormControlLabel
              key={device.device_id}
              control={
                <Checkbox
                  checked={selectedIds.has(device.device_id)}
                  onChange={() => {
                    setSelectedIds((prev) => {
                      const next = new Set(prev);
                      if (next.has(device.device_id)) next.delete(device.device_id);
                      else next.add(device.device_id);
                      return next;
                    });
                  }}
                />
              }
              label={`${device.device_name} (${device.device_type})`}
            />
          ))
        )}
        <Button
          variant="contained"
          onClick={handleSendTest}
          disabled={selectedIds.size === 0 || sendingTest}
          sx={{ mt: 2, display: 'block' }}
        >
          {sendingTest ? 'Sending…' : 'Send test notification'}
        </Button>
      </Paper>
    </Box>,
  );
};

export default withRouter(UserPreferencesPage);
